import { ReactNode } from "react";

interface SimpleTooltipProps {
  content: ReactNode;
  children: ReactNode;
  placement?: "top" | "bottom" | "left" | "right";
  /** When true, allows wrapping up to 220px wide. Default: single-line whitespace-nowrap */
  wrap?: boolean;
  className?: string;
}

const placementClasses: Record<string, string> = {
  top: "bottom-full left-1/2 -translate-x-1/2 mb-2",
  bottom: "top-full left-1/2 -translate-x-1/2 mt-2",
  left: "right-full top-1/2 -translate-y-1/2 mr-2",
  right: "left-full top-1/2 -translate-y-1/2 ml-2",
};

// Arrow points away from the tooltip (toward the trigger)
const arrowPlacementClasses: Record<string, string> = {
  top: "top-full left-1/2 -translate-x-1/2",
  bottom: "bottom-full left-1/2 -translate-x-1/2",
  left: "left-full top-1/2 -translate-y-1/2",
  right: "right-full top-1/2 -translate-y-1/2",
};

const arrowBorderClasses: Record<string, string> = {
  top: "border-l-transparent border-r-transparent border-b-transparent border-t-[6px] border-l-[5px] border-r-[5px] border-t-gray-800 dark:border-t-neutral-600",
  bottom: "border-l-transparent border-r-transparent border-t-transparent border-b-[6px] border-l-[5px] border-r-[5px] border-b-gray-800 dark:border-b-neutral-600",
  left: "border-t-transparent border-b-transparent border-r-transparent border-l-[6px] border-t-[5px] border-b-[5px] border-l-gray-800 dark:border-l-neutral-600",
  right: "border-t-transparent border-b-transparent border-l-transparent border-r-[6px] border-t-[5px] border-b-[5px] border-r-gray-800 dark:border-r-neutral-600",
};

const SimpleTooltip = ({
  content,
  children,
  placement = "top",
  wrap = false,
  className = "",
}: SimpleTooltipProps) => {
  return (
    <div className={`relative group/tip inline-flex ${className}`}>
      {children}
      <div
        className={`absolute ${placementClasses[placement]} z-50 pointer-events-none
          opacity-0 group-hover/tip:opacity-100 transition-opacity duration-150`}
      >
        {/* Tooltip bubble — light: dark bg / dark: slightly lighter */}
        <div
          className={`
            bg-gray-800 dark:bg-neutral-600
            text-white dark:text-gray-100
            text-xs font-medium
            rounded-lg px-2.5 py-1.5 shadow-lg
            ${wrap ? "w-max max-w-[220px] break-words text-center" : "whitespace-nowrap"}
          `}
        >
          {content}
        </div>
        {/* Arrow */}
        <div
          className={`absolute w-0 h-0 ${arrowPlacementClasses[placement]} ${arrowBorderClasses[placement]}`}
        />
      </div>
    </div>
  );
};

export default SimpleTooltip;
